import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import CharacterList from "../character-list/CharacterListComponent";
import ToggleButton from "../ui/toogle-button/ToggleButtonComponent";
import { hiraganas } from "../../consts/hiraganaList";
import kanjiDictionary from "../../consts/kanjiDictionary";
import { buildRomajiMap, convertRomajiValue } from "../../utils/romajiConverter";
import Seo from "../Seo/SeoComponent";
import * as analytics from "../../utils/analytics";
import styles from "./converterPage.module.scss";
import PageLanguageLink from "../PageLanguageLink/PageLanguageLink";

type Mode = "hiraganaToKanji" | "kanjiToHiragana";
type KeyboardSection = "Basic" | "Voiced" | "Combinations" | "Punctuation";

const examplesByMode: Record<Mode, string[]> = {
    hiraganaToKanji: ["たべます", "のみます", "はなせます", "いきます"],
    kanjiToHiragana: ["食べます", "飲みます", "話せます", "行きます"]
};

const keyboardSectionKeys: KeyboardSection[] = ["Basic", "Voiced", "Combinations", "Punctuation"];

function ConverterPage() {
    const { t, i18n } = useTranslation("converterPage");
    const [mode, setMode] = useState<Mode>("hiraganaToKanji");
    const [input, setInput] = useState("");
    const [selectedText, setSelectedText] = useState("");
    const [showKeyboard, setShowKeyboard] = useState(true);
    const [copyError, setCopyError] = useState(false);
    const [activeKeyboardSection, setActiveKeyboardSection] = useState<KeyboardSection>("Basic");
    const keyboardRef = useRef<HTMLDivElement>(null);

    const isPt = i18n.language === "pt";
    const switchPath = isPt ? "/converter" : "/pt/converter";
    const currentPath = isPt ? "/pt/converter" : "/converter";

    const romajiMap = useMemo(() => buildRomajiMap(hiraganas), []);

    const kanjiToHiraganaMap = useMemo(() => {
        const map = new Map<string, string[]>();
        kanjiDictionary.forEach((entry) => {
            entry.candidates.forEach((candidate) => {
                const existing = map.get(candidate) || [];
                if (!existing.includes(entry.reading)) {
                    map.set(candidate, [...existing, entry.reading]);
                }
            });
        });
        return map;
    }, []);

    const content = t(`modes.${mode}`, { returnObjects: true }) as {
        title: string;
        description: string;
        info: string;
        inputLabel: string;
        placeholder: string;
        resultsTitle: string;
        resultsHelp: string;
        emptyState: string;
        outputTitle: string;
    };
    const examples = examplesByMode[mode];

    const suggestions = useMemo(() => {
        const normalized = input.trim();

        if (!normalized) return [];

        if (mode === "kanjiToHiragana") {
            const exactMatch = kanjiToHiraganaMap.get(normalized);

            if (exactMatch) return exactMatch;

            return [
                ...new Set(
                    Array.from(kanjiToHiraganaMap.entries())
                        .filter(([candidate]) => candidate.startsWith(normalized))
                        .flatMap(([, readings]) => readings)
                )
            ];
        }

        let searchText = normalized;

        if (/[a-z]/i.test(normalized)) {
            const romajiValue = normalized.toLowerCase().replace(/\s+/g, "");
            const { produced } = convertRomajiValue(romajiValue, romajiMap, false);
            if (produced) {
                searchText = produced;
            }
        }

        const exactMatch = kanjiDictionary.find((entry) => entry.reading === searchText);

        if (exactMatch) return exactMatch.candidates;

        return [
            ...new Set(
                kanjiDictionary
                    .filter((entry) => entry.reading.startsWith(searchText))
                    .flatMap((entry) => entry.candidates)
            )
        ];
    }, [input, romajiMap, mode, kanjiToHiraganaMap]);

    function handleModeChange(nextMode: Mode) {
        if (nextMode === mode) return;
        setMode(nextMode);
        setInput("");
        setSelectedText("");
    }

    function handleSelectCandidate(candidate: string) {
        setSelectedText(candidate);
        analytics.track("converter_used", {
            mode,
            inputLength: input.trim().length,
            resultCount: suggestions.length,
            found: suggestions.length > 0,
        });
    }

    function handleClear() {
        setInput("");
        setSelectedText("");
    }

    function handleCopy() {
        const valueToCopy = selectedText || input;

        if (!valueToCopy) return;

        navigator.clipboard
            .writeText(valueToCopy)
            .then(() => setCopyError(false))
            .catch(() => setCopyError(true));
    }

    function handleCharacterClick(char: string) {
        setInput((prev) => prev + char);
        setSelectedText("");
    }

    function handleDeleteLast() {
        setInput((prev) => prev.slice(0, -1));
        setSelectedText("");
    }

    function handleExampleClick(word: string) {
        setInput(word);

        if (mode === "kanjiToHiragana") {
            const readings = kanjiToHiraganaMap.get(word);
            setSelectedText(readings ? readings[0] : "");
            return;
        }

        const exactMatch = kanjiDictionary.find((entry) => entry.reading === word);
        setSelectedText(exactMatch ? exactMatch.candidates[0] : "");
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.pageHeader}>
                    <div className={styles.pageHeaderIntro}>
                        <h1 className={styles.pageTitle}>{content.title}</h1>
                        <p className={styles.pageDescription}>{content.description}</p>
                    </div>

                    <div className={styles.pageInfo}>{content.info}</div>
                </section>

                <div className={styles.innerGrid}>
                    <section
                        className={`${styles.converterPanel} ${
                            mode === "kanjiToHiragana" ? styles.converterPanelFull : ""
                        }`}
                    >
                        <div className={styles.panelCard}>
                            <div className={styles.panelHeader}>
                                <p className={styles.panelTag}>{t("input")}</p>
                                <div className={styles.modeTabs} role="tablist">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === "hiraganaToKanji"}
                                        className={`${styles.modeTab} ${
                                            mode === "hiraganaToKanji" ? styles.modeTabActive : ""
                                        }`}
                                        onClick={() => handleModeChange("hiraganaToKanji")}
                                    >
                                        {t("modes.hiraganaToKanji.tab")}
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={mode === "kanjiToHiragana"}
                                        className={`${styles.modeTab} ${
                                            mode === "kanjiToHiragana" ? styles.modeTabActive : ""
                                        }`}
                                        onClick={() => handleModeChange("kanjiToHiragana")}
                                    >
                                        {t("modes.kanjiToHiragana.tab")}
                                    </button>
                                </div>
                            </div>

                            <label htmlFor="converter-input" className={styles.fieldLabel}>
                                {content.inputLabel}
                            </label>
                            <textarea
                                id="converter-input"
                                value={input}
                                onChange={(e) => {
                                    setInput(e.target.value);
                                    setSelectedText("");
                                }}
                                placeholder={content.placeholder}
                                className={styles.textarea}
                                rows={5}
                            />

                            <div className={styles.examplesSection}>
                                <div className={styles.examplesHeader}>
                                    <span>{t("examples")}</span>
                                    <span className={styles.examplesNote}>{t("tapToFill")}</span>
                                </div>
                                <div className={styles.examplesRow}>
                                    {examples.map((word) => (
                                        <button
                                            key={word}
                                            type="button"
                                            className={styles.exampleButton}
                                            onClick={() => handleExampleClick(word)}
                                        >
                                            {word}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={styles.actionRow}>
                                <button
                                    type="button"
                                    className={`${styles.actionButton} ${styles.neutralButton}`}
                                    onClick={handleDeleteLast}
                                    disabled={!input}
                                >
                                    {t("delete")}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.actionButton} ${styles.secondaryButton}`}
                                    onClick={handleClear}
                                    disabled={!input && !selectedText}
                                >
                                    {t("clear")}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.actionButton} ${styles.primaryButton}`}
                                    onClick={handleCopy}
                                    disabled={!input && !selectedText}
                                >
                                    {t("copy")}
                                </button>
                            </div>
                        </div>

                        <div className={styles.panelCard}>
                            <div className={styles.sectionHeaderRow}>
                                <h3 className={styles.sectionTitle}>{content.resultsTitle}</h3>
                                <p className={styles.sectionHelp}>{content.resultsHelp}</p>
                            </div>

                            {suggestions.length > 0 ? (
                                <div className={styles.suggestions}>
                                    {suggestions.map((candidate) => (
                                        <button
                                            key={candidate}
                                            type="button"
                                            className={styles.suggestionButton}
                                            onClick={() => handleSelectCandidate(candidate)}
                                        >
                                            {candidate}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    <p>{content.emptyState}</p>
                                </div>
                            )}
                        </div>

                        {(selectedText || input) && (
                            <div className={styles.panelCard}>
                                <div className={styles.resultHeader}>
                                    <div>
                                        <p className={styles.panelTag}>{t("result")}</p>
                                        <h3 className={styles.sectionTitle}>{content.outputTitle}</h3>
                                    </div>
                                    <button
                                        type="button"
                                        className={`${styles.actionButton} ${styles.secondaryButton}`}
                                        onClick={handleCopy}
                                    >
                                        {t("copy")}
                                    </button>
                                </div>
                                <div className={styles.resultBox}>{selectedText || input}</div>
                                {copyError && (
                                    <p className={styles.copyErrorText} role="alert">
                                        {t("copyError")}
                                    </p>
                                )}
                            </div>
                        )}
                    </section>

                    {mode === "hiraganaToKanji" && (
                        <aside className={styles.keyboardAside}>
                            <div className={styles.keyboardCard}>
                                <div className={styles.keyboardHeader}>
                                    <div>
                                        <p className={styles.panelTag}>{t("keyboard")}</p>
                                        <h2 className={styles.panelTitle}>{t("hiraganaKeyboard")}</h2>
                                    </div>
                                    <div className={styles.toggleWrapper}>
                                        <ToggleButton
                                            label={t("showHiraganaKeyboard")}
                                            className={styles.toggle}
                                            value={showKeyboard}
                                            showIcons={false}
                                            onChange={() => setShowKeyboard(!showKeyboard)}
                                        />
                                    </div>
                                </div>

                                {showKeyboard ? (
                                    <div ref={keyboardRef} className={styles.keyboardSection}>
                                        <div className={styles.keyboardTabs} role="tablist">
                                            {keyboardSectionKeys.map((section) => (
                                                <button
                                                    key={section}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={activeKeyboardSection === section}
                                                    className={`${styles.keyboardTab} ${
                                                        activeKeyboardSection === section
                                                            ? styles.keyboardTabActive
                                                            : ""
                                                    }`}
                                                    onClick={() => setActiveKeyboardSection(section)}
                                                >
                                                    {t(`keyboardSections.${section}`)}
                                                </button>
                                            ))}
                                        </div>

                                        <CharacterList
                                            data-testid="converter-hiragana-list"
                                            characters={hiraganas}
                                            rows={6}
                                            activeSection={activeKeyboardSection}
                                            handleChange={handleCharacterClick}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.emptyState}>
                                        <p>{t("keyboardHidden")}</p>
                                    </div>
                                )}
                            </div>
                        </aside>
                    )}
                </div>

                <PageLanguageLink to={switchPath} />
            </main>

            <Footer />
        </div>
    );
}

export default ConverterPage;
